import { useGetFeaturesQuery } from '../api/features/getFeatures';
import { useMemo } from 'react';
import { useWelcomeQuery } from '../api/getWelcomeDetails';

/**
 * Gates the club styling that ships with the mobile webview (TLC-4294). Unlike
 * the flags above this one is not served by mypolicy-api-features, it is set
 * per environment with `VITE_ENABLE_MOBILE_WEBVIEW_UI` in the matching
 * `env/.env.*` file.
 *
 * Exported on its own as well as through the hook so the club theme options,
 * which run at module scope, can read it too.
 */
export const enableMobileWebviewUi =
  import.meta.env.VITE_ENABLE_MOBILE_WEBVIEW_UI === 'true';

/**
 * Gates the Apple Wallet / ID cards experience (TLC-4294): the `/id-cards`
 * route and the chip that links to it from the document library. Set per
 * environment with `VITE_ENABLE_APPLE_WALLET_ID_CARDS` in the matching
 * `env/.env.*` file.
 *
 * Exported on its own as well as through the hook so the route config in
 * `router.tsx`, which runs at module scope, can read it too.
 */
export const enableAppleWalletIdCards =
  import.meta.env.VITE_ENABLE_APPLE_WALLET_ID_CARDS === 'true';

const useGetFeatureFlags = () => {
  const { data: welcomeData } = useWelcomeQuery();
  const { data: featuresData, isLoading } = useGetFeaturesQuery(!!welcomeData);

  const mprFlags = useMemo(
    () => ({
      enablePayNow: featuresData?.enablePayNow ?? false,
      enablePBEEsign: featuresData?.enablePBEEsign ?? false,
      enableServiceText: featuresData?.enableServiceText ?? false,
      disableChat: featuresData?.disableChat ?? false,
      enableSettings: featuresData?.enableSettings ?? false,
      enableContactUsTextSection:
        featuresData?.enableContactUsTextSection ?? false,
      // NEXTGEN-FLAG: on flag retirement, remove this key and every consumer's
      // `enableNextGen &&` guard (NG behavior becomes unconditional).
      enableNextGen: featuresData?.enableNextGen ?? false,
    }),
    [featuresData]
  );

  return {
    ...mprFlags,
    enableMobileWebviewUi,
    enableAppleWalletIdCards,
    isLoading,
  };
};

export default useGetFeatureFlags;
